import React, { FunctionComponent, useContext, useEffect } from 'react'
import { useTranslation } from '../../utils/convergence'
import {
  useApplePayState,
  useCheckoutState,
  useHasUserInitiatedChangedPaymentMethod,
  useInlineArcadeComponentsEnabled,
  useIsAfterpayFinanceFeeEligible,
  usePreferredBankAccount,
  usePreferredCard,
  usePreferredPaymentMethod,
  useSelectedPaymentType,
} from '../../state'
import { isCardScanRequiredErrorCode, PaymentMethodType, RecoverableErrorCode, ApplePayError, hasCardTopazErrorReason } from '../../model'
import { ErrorCode } from '@afterpaytouch/portal-api'
import { isNullOrUndefined, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { ErrorText, Trans } from '../'
import { isKeyWithInlineContext } from './utils'
import { SwitchContext } from './types'
import styles from './style.module.css'
import { Link, Text } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../utils/responsive'
import { useAddPaymentLinkHandler } from './hooks'
import { SummaryNotificationContext } from './SummaryNotificationContext'
import { buildTranslationKey } from '../../utils/locales'
import { PaymentType } from '@afterpay/types'

const getPreferredPaymentMethodKey = (paymentMethodType: PaymentMethodType | undefined): string => {
  switch (paymentMethodType) {
    case PaymentMethodType.ACH_BANK_ACCOUNT:
      return 'bank'
    case PaymentMethodType.APPLE_PAY:
      return 'applePay'
    default:
      return 'card'
  }
}

export const SummaryNotification: FunctionComponent = () => {
  const { setNotification, setClicked, notification } = useContext(SummaryNotificationContext)
  const { t } = useTranslation()
  const { card, valid, reason, switched } = usePreferredCard() ?? {}
  const { account, valid: bankAccountValid, reason: bankAccountReason, switched: bankAccountSwitched } = usePreferredBankAccount() ?? {}
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const hasUserInitiatedChangedPaymentMethod = useHasUserInitiatedChangedPaymentMethod()
  const { error } = useCheckoutState()
  const applepay = useApplePayState()
  const showNotificationForNonSwitchedTopazError =
    !hasUserInitiatedChangedPaymentMethod && preferredPaymentMethod === PaymentMethodType.CREDIT_CARD ? !switched && hasCardTopazErrorReason(card) : false
  const isInlineArcadeComponentEnabled = useInlineArcadeComponentsEnabled()
  const { smallTextSize } = useCheckoutSizes()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const selectedPaymentType = useSelectedPaymentType()
  const isAFFPBI = isAFF && selectedPaymentType !== PaymentType.PCL
  const addPaymentMethodHandler = useAddPaymentLinkHandler()

  const getMessageWithLink = (messageKey: string, context?: string): React.ReactNode => {
    return (
      <Text size={smallTextSize} testNameSpace='summary-notification'>
        <Trans
          i18nKey={messageKey}
          context={context}
          components={{
            addPaymentMethodLink: <Link kind='Underline' testNameSpace='summary-notification' onClick={addPaymentMethodHandler} target='_blank' />,
          }}
        />
      </Text>
    )
  }

  // clear notification conditions
  useEffect(() => {
    const shouldClearNotification =
      valueIsNotNullOrUndefined(notification) && isNullOrUndefined(error) && isNullOrUndefined(applepay.error) && isNullOrUndefined(reason)
    if (shouldClearNotification) {
      setNotification(undefined)
    }
  }, [notification, error, applepay.error, setNotification, reason])

  // errors on preferred payment method
  useEffect(() => {
    if (preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && !isNullOrUndefined(account) && !isNullOrUndefined(bankAccountReason)) {
      const switchContext = bankAccountSwitched ? SwitchContext.Switched : SwitchContext.CannotSwitch

      // The "switched" message only exists for inline arcade components.
      const context = isInlineArcadeComponentEnabled && bankAccountSwitched && isKeyWithInlineContext(bankAccountReason, switchContext) ? 'switchedInline' : ''

      const messageKey = buildTranslationKey({
        namespace: 'summary',
        segments: [
          'bankAccountErrors',
          bankAccountReason,
          {
            contexts: [[isAFFPBI && bankAccountReason === 'noValidCard', `message_aff`]],
            default: 'message',
          },
        ],
      })
      setNotification({
        heading: t(`summary:bankAccountErrors.${bankAccountReason}.heading`),
        message: getMessageWithLink(messageKey, context),
        kind: 'Error',
        severe: !bankAccountValid,
      })
    } else if (!isNullOrUndefined(card) && !isNullOrUndefined(reason) && !hasUserInitiatedChangedPaymentMethod) {
      const baseContext = switched ? SwitchContext.Switched : SwitchContext.CannotSwitch
      const context = isInlineArcadeComponentEnabled && isKeyWithInlineContext(reason, baseContext) ? `${baseContext}Inline` : baseContext
      const messageKey = buildTranslationKey({
        namespace: 'summary',
        segments: [
          'error',
          reason,
          {
            contexts: [[isAFFPBI && reason === 'creditCardNotAllowed', `message_aff_${getPreferredPaymentMethodKey(preferredPaymentMethod)}`]],
            default: 'message',
          },
        ],
      })

      const message = isInlineArcadeComponentEnabled ? getMessageWithLink(messageKey, context) : t(messageKey, { context })

      const inlineKind = baseContext === SwitchContext.CannotSwitch ? 'Error' : 'Info'

      setNotification({
        heading: t(`summary:error.${reason}.heading`, { context: baseContext }),
        message,
        kind: isInlineArcadeComponentEnabled ? inlineKind : 'Error',
        severe: !valid,
      })
    }
  }, [
    card,
    valid,
    reason,
    switched,
    account,
    bankAccountValid,
    bankAccountSwitched,
    preferredPaymentMethod,
    bankAccountReason,
    hasUserInitiatedChangedPaymentMethod,
    isInlineArcadeComponentEnabled,
    setNotification,
  ])

  // errors set by checkout & on confirm
  useEffect(() => {
    if (error?.error !== 'undefined' && isCardScanRequiredErrorCode(error?.error as ErrorCode)) {
      return setClicked(false)
    } else if (typeof error?.error !== 'undefined' && (error?.error as ErrorCode) === ErrorCode.CardNotEnabled) {
      // confirm can trigger card_not_enabled if the card is not a supported brand
      setClicked(false)
    } else if (typeof error?.error !== 'undefined' && Object.values(RecoverableErrorCode).includes(error?.error as RecoverableErrorCode)) {
      setClicked(false)
      const messageKey = buildTranslationKey({
        namespace: 'summary',
        segments: [
          'error',
          error?.error,
          {
            contexts: [[isAFFPBI, `message_aff`]],
            default: 'message',
          },
        ],
      })
      const inlineContext = isKeyWithInlineContext(error.error) ? 'inline' : undefined

      const message = isInlineArcadeComponentEnabled ? getMessageWithLink(messageKey, inlineContext) : t(messageKey)

      setNotification({
        heading: t(`summary:error.${error?.error}.heading`),
        message,
        kind: 'Error',
        severe: false,
      })
    } else if (typeof applepay.error !== 'undefined') {
      setClicked(false)
      if (applepay?.error === ApplePayError.InvalidCardType) {
        setNotification({
          heading: t('summary:error.prepaidCard.heading'),
          message: t('summary:error.prepaidCard.message'),
          kind: 'Error',
          severe: true,
        })
      } else {
        setNotification({
          heading: t('summary:error.applePay.heading'),
          message: t('summary:error.applePay.message'),
          kind: 'Error',
          severe: true,
        })
      }
    }
  }, [error, applepay.error, isInlineArcadeComponentEnabled, setClicked, setNotification])

  const containerClass = useInlineArcadeComponentsEnabled() ? styles.inlineContainer : styles.container

  // (Non-inline) Some topaz errors are shown elsewhere in the UI, so we don't want to show them here
  const nonInlineShouldShowNotification = notification !== undefined && !showNotificationForNonSwitchedTopazError

  // (Inline) All errors are shown in the summary notification
  const inlineShouldShowNotification = notification !== undefined

  const shouldShowNotification = isInlineArcadeComponentEnabled ? inlineShouldShowNotification : nonInlineShouldShowNotification

  return shouldShowNotification ? (
    <div className={containerClass}>
      <ErrorText testNamespace='summary-notification' message={notification?.message} kind={notification?.kind} />
    </div>
  ) : null
}
